/**
 * Beställningar av häften.
 *
 * Föreningen måste få in pengarna innan häftena lämnas ut. Idag löses det
 * genom att häftena delas ut på papper och betalningen jagas i efterhand,
 * vilket skapar en skuld som lagledaren får driva in.
 *
 * En beställning vänder på ordningen: koderna finns men delas inte ut förrän
 * betalningen är bekräftad. Den som inte betalar har inte fått något, och då
 * finns ingenting att jaga – bara att påminna om.
 *
 * Referensen är hela poängen med modellen. Varje beställning bär en kort,
 * unik sträng som följer med i Swish-meddelandet. Avstämningen blir därmed en
 * uppslagning i stället för ett detektivarbete, och det spelar ingen roll om
 * bekräftelsen kommer från en lagledare som tittar i telefonen, från en
 * uppladdad Swish-rapport eller från ett API – de skriver alla samma fält.
 */

import { CODE_ALPHABET } from './codes'

/** Prefix i Swish-meddelandet, så mottagaren ser vad betalningen gäller. */
export const ORDER_PREFIX = 'LIRA'

/**
 * Referensens längd.
 *
 * Sex tecken ur ett 32-teckens alfabet ger drygt en miljard kombinationer.
 * Det handlar inte om att gissningar ska vara svåra – referensen är inte en
 * hemlighet – utan om att två beställningar samma säsong aldrig ska krocka,
 * och om att den ska gå att läsa upp i telefon utan att bli fel.
 */
export const ORDER_REFERENCE_LENGTH = 6

export type OrderStatus = 'awaitingPayment' | 'paid' | 'cancelled'

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  awaitingPayment: 'Väntar på betalning',
  paid: 'Betald',
  cancelled: 'Avbruten',
}

/** Hur betalningen bekräftades – syns i loggen och i lagledarens lista. */
export type OrderPaymentSource = 'manual' | 'swishReport' | 'swishApi'

export const ORDER_PAYMENT_SOURCE_LABELS: Record<OrderPaymentSource, string> = {
  manual: 'Bekräftad för hand',
  swishReport: 'Matchad mot Swish-rapport',
  swishApi: 'Bekräftad av Swish',
}

/**
 * Genererar en referens.
 *
 * Samma alfabet som rabattkoderna, alltså utan I, L, O och U. En förälder
 * som läser upp referensen i telefon ska inte kunna blanda ihop noll och O.
 */
export function generateOrderReference(randomBytes: (n: number) => Uint8Array): string {
  const bytes = randomBytes(ORDER_REFERENCE_LENGTH)
  let reference = ''
  for (let i = 0; i < ORDER_REFERENCE_LENGTH; i++) {
    reference += CODE_ALPHABET[bytes[i] % CODE_ALPHABET.length]
  }
  return reference
}

/** Meddelandet som ska stå i Swish-betalningen. */
export function orderPaymentMessage(reference: string): string {
  return `${ORDER_PREFIX} ${reference}`
}

/**
 * Plockar ut referensen ur ett Swish-meddelande.
 *
 * Toleransen är medveten. Meddelandefältet går att redigera i vissa
 * bankappar, folk skriver "Lira 7k2mq9" med gemener eller lägger till sitt
 * barns namn, och en avstämning som bara matchar exakta strängar hade lämnat
 * kvar just de betalningar som kostar mest tid att reda ut för hand.
 *
 * Returnerar null när ingenting som liknar en referens går att hitta.
 */
export function parseOrderReference(message: string): string | null {
  const prefix = ORDER_PREFIX.toUpperCase()
  const isReference = new RegExp('^[' + CODE_ALPHABET + ']{' + ORDER_REFERENCE_LENGTH + '}$')

  /*
   * Meddelandet delas i ord, och bara hela ord duger som referens.
   *
   * Tidigare letade tolkningen efter vilken sexteckensbit som helst var som
   * helst i texten. Det plockade ut referenser ur vanliga svenska ord: en
   * medlem som swishade med meddelandet "Traningsavgift host" fick raden
   * klassad som "okänd referens", eftersom NGSAVG råkar bestå av tecken som
   * ingår i alfabetet. Ingen sådan rad har någonsin bockats av automatiskt –
   * beloppet måste stämma också – men lagledaren fick en varningsrad att
   * utreda för varje träningsavgift i rapporten.
   *
   * Å, Ä och Ö räknas som ordtecken när vi delar. Annars skulle "häftena"
   * falla isär i bitar som var för sig ser ut som referenser.
   */
  const words = (message ?? '')
    .toUpperCase()
    .split(/[^0-9A-ZÅÄÖ]+/)
    .filter(Boolean)

  // Först det som står efter prefixet, med eller utan mellanrum: "LIRA 7K2MQ9"
  // och "LIRA7K2MQ9" är samma sak för den som skrivit det.
  for (let index = 0; index < words.length; index++) {
    const word = words[index]
    if (word === prefix) {
      const next = words[index + 1]
      if (next && isReference.test(next)) return next
    } else if (word.startsWith(prefix)) {
      const rest = word.slice(prefix.length)
      if (isReference.test(rest)) return rest
    }
  }

  // Sedan ett ensamt ord som är referensen. Många skriver bara den, ofta med
  // barnets namn bredvid.
  return words.find((word) => isReference.test(word)) ?? null
}

/**
 * Nyckelns längd, i tecken ur kodalfabetet.
 *
 * Tjugofyra tecken ur ett alfabet på trettiotvå är 120 bitar. Det är inte
 * valt för att se imponerande ut utan för att stänga en dörr som stod öppen:
 * referensen är sex tecken, och sex tecken går att räkna upp.
 */
export const ORDER_TOKEN_LENGTH = 24

/**
 * Nyckeln som ger tillgång till en beställning utan inloggning.
 *
 * Referensen räcker inte. Den är sex tecken lång för att en förälder ska orka
 * skriva av den i ett Swish-meddelande, och den designen är riktig – men den
 * gör också sökrymden till en miljard, och Firestores batchGet utvärderar
 * hundratals dokument-id per anrop. Att gissa en i taget är hopplöst; att
 * beta av hela collectionen med några hundra anrop är det inte.
 *
 * Därför bär länken i mejlet en egen nyckel. Referensen fortsätter vara det
 * man skriver i betalningen, nyckeln är det som öppnar sidan, och den som
 * bara har den ena kommer ingenstans.
 */
export function generateOrderToken(randomBytes: (n: number) => Uint8Array): string {
  const bytes = randomBytes(ORDER_TOKEN_LENGTH)
  let out = ''
  for (let i = 0; i < ORDER_TOKEN_LENGTH; i++) out += CODE_ALPHABET[bytes[i] % CODE_ALPHABET.length]
  return out
}

export interface Order {
  /** Dokument-id är referensen, t.ex. "7K2MQ9". */
  id: string
  /** Nyckeln i länken. Referensen ensam ger ingen tillgång. */
  accessToken?: string
  bookletId: string
  periodName: string
  teamId: string
  teamName: string
  /** Vem häftena tillhör. Fritext – föreningen har inget spelarregister. */
  playerName: string
  /** Föräldern som betalar och tar emot koderna. */
  buyerName: string
  buyerEmail: string
  quantity: number
  /** Styckpris vid beställningstillfället, så att en prisändring inte
   *  ändrar vad någon redan har beställt. */
  unitPriceSek: number
  totalSek: number
  status: OrderStatus
  /** Swish-numret beställningen ska betalas till, om laget använder ett. */
  swishNumber: string
  /** Betallänk till Digitala Lagkassan eller motsvarande, om laget har en. */
  paymentUrl?: string | null
  /** Fylls i när betalningen bekräftats. */
  paidAt?: unknown
  paidBy?: string | null
  paymentSource?: OrderPaymentSource | null
  /** Koderna som delades ut. Tomt tills beställningen är betald. */
  codeIds?: string[]
  cancelledAt?: unknown
  cancelledBy?: string | null
  cancelReason?: string | null
  /** Senaste påminnelsen, så att lagledaren inte mejlar samma dag två gånger. */
  remindedAt?: unknown
  isTest?: boolean
  createdAt: unknown
}

/** Övre gräns per beställning. */
export const MAX_ORDER_QUANTITY = 50

/**
 * Kontrollerar en beställning innan den skapas.
 *
 * Antalet spärras inte mot lagets tilldelning. Föreningen sätter ett fast
 * antal per spelare som förväntan, men en förälder som vill sälja fler ska
 * kunna ta av lagets pott – att bromsa den som är mest engagerad vore det
 * sista man vill göra. Taket ovan finns bara för att fånga feltryck.
 *
 * Returnerar ett svenskt felmeddelande, eller null när allt stämmer.
 */
export function validateOrder(input: {
  buyerName: string
  buyerEmail: string
  playerName: string
  quantity: number
}): string | null {
  if (!input.buyerName?.trim()) return 'Fyll i ditt namn.'
  if (!input.playerName?.trim()) return 'Fyll i spelarens namn.'

  const email = input.buyerEmail?.trim() ?? ''
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    return 'E-postadressen ser inte riktig ut. Kontrollera stavningen.'
  }

  if (!Number.isInteger(input.quantity) || input.quantity < 1) {
    return 'Antalet häften måste vara minst 1.'
  }
  if (input.quantity > MAX_ORDER_QUANTITY) {
    return `Det går att beställa som mest ${MAX_ORDER_QUANTITY} häften åt gången. Hör av dig till kansliet om ni behöver fler.`
  }

  return null
}

/* ------------------------------------------------------------------ *
 * Swish
 * ------------------------------------------------------------------ */

/**
 * Innehållet i en Swish-QR med förifyllda uppgifter.
 *
 * Formatet är Swish "C-sträng": `C<nummer>;<belopp>;<meddelande>;<lås>`.
 * Låsmasken är en bitmask där 1 låser mottagaren, 2 låser beloppet och 4
 * låser meddelandet – summan 7 låser alltså allt tre, vilket är precis vad
 * avstämningen kräver. Ändras meddelandet går referensen förlorad och
 * betalningen måste redas ut för hand.
 *
 * VIKTIGT: formatet är läst ur Swish egen dokumentation men inte verifierat
 * mot en skarp betalning i det här projektet. Testa med en krona innan ni
 * skickar ut det till föräldrar. Beloppet, numret och referensen står också
 * som text i mejlet och på beställningssidan, så en QR som inte skulle
 * fungera gör inte beställningen obetalbar.
 */
export function swishQrPayload(options: {
  swishNumber: string
  amountSek: number
  message: string
}): string {
  const number = options.swishNumber.replace(/\D/g, '')
  return `C${number};${options.amountSek};${options.message};7`
}

/**
 * Länk som öppnar Swish-appen direkt.
 *
 * Behövs för att en förälder som beställer i mobilen inte kan skanna sin egen
 * skärm. QR-koden är till för den som sitter vid datorn.
 */
export function swishAppLink(options: {
  swishNumber: string
  amountSek: number
  message: string
}): string {
  const data = {
    version: 1,
    payee: { value: options.swishNumber.replace(/\D/g, '') },
    amount: { value: options.amountSek },
    message: { value: options.message },
  }
  return `swish://payment?data=${encodeURIComponent(JSON.stringify(data))}`
}

/**
 * Hur en beställning ska betalas.
 *
 * Ett lag betalar antingen in i Digitala Lagkassan via en länk, eller till
 * ett Swish-nummer. Länken vinner när båda finns: den pekar på ett system som
 * bokför betalningen där lagkassan faktiskt är, medan ett Swish-nummer bara
 * flyttar pengar och lämnar bokföringen till någon annan.
 *
 * Referensen följer med i båda fallen. Den är det som gör avstämningen till
 * en uppslagning oavsett vilken väg pengarna tagit.
 */
export function paymentMethodFor(order: {
  paymentUrl?: string | null
  swishNumber?: string | null
}): 'link' | 'swish' | 'none' {
  if ((order.paymentUrl ?? '').trim()) return 'link'
  if ((order.swishNumber ?? '').trim()) return 'swish'
  return 'none'
}
